/**
 * Reine Logik des virtuellen Postfachs in der Kopfzeile (Musterkatalog M-067, Design-Guide `dev-postfach.md`). Keine
 * Rechte-Entscheidung: ob die Pille ausgeliefert wird, welche Personen wählbar sind und was gelesen werden darf, entscheidet
 * der Server (`pruefeDevWerkzeugeZugang` + `darfPostfachLesen`); dieser Baustein bekommt nur das Ergebnis.
 */

export type PostfachKanal = "mail" | "kalender" | "teams" | "push" | "glocke" | "webhook";

/** Antwortform der Server-Actions (strukturgleich zu `lib/dev-postfach/typen.ts`). */
export type PostfachAntwortForm<T> = ({ ok: true } & T) | { ok: false; art: "abgelehnt" | "fehlgeschlagen"; meldung: string };

export interface PostfachEintrag {
  id: string;
  kanal: PostfachKanal;
  quelle: string;
  status: string;
  erstellt: string;
  /** `null` = nicht ausgeliefert (fremdes Postfach, Impersonation): kein Punkt, kein Fettdruck. */
  gelesen: boolean | null;
  betreff: string;
  vorschau: string;
  an: string[];
}

export interface PostfachDetailDaten extends PostfachEintrag {
  von: string | null;
  cc: string[];
  bcc: string[];
  text: string;
  html: string | null;
  anhaenge: { name: string; typ: string; groesse: number }[];
  hinweis: string | null;
  geschwaerzt: boolean;
}

export interface PostfachPerson {
  id: string;
  name: string | null;
  eigen: boolean;
  /** Gesamtzahl der Nachrichten im gewählten Zeitraum (nur diese Zahl, nie ein Lesestand, r176/r177); fehlt = ohne Zahl. */
  anzahl?: number;
}

export interface PostfachFilter {
  zielUserId?: string;
  kanal?: PostfachKanal;
  suche?: string;
  von?: string;
  limit?: number;
}

/** Die Server-Actions, die der Aufrufer hereinreicht (jede prüft selbst, r109). */
export interface PostfachAktionen {
  /** `von`: ISO-Untergrenze des Zeitraums für die Gesamtzahl je Person (der Server begrenzt auf die Aufbewahrung). */
  personen: (suche?: string, von?: string) => Promise<PostfachAntwortForm<{ personen: PostfachPerson[] }>>;
  liste: (filter?: PostfachFilter) => Promise<PostfachAntwortForm<{ zielUserId: string; eintraege: PostfachEintrag[]; ungelesen: number | null }>>;
  detail: (id: string) => Promise<PostfachAntwortForm<{ detail: PostfachDetailDaten }>>;
  /** Eigenes Postfach leeren (für alle mit Zugang, nie ein fremdes). */
  leereEigenes: (zielUserId?: string) => Promise<PostfachAntwortForm<{ geloescht: number }>>;
  /** Alle Postfächer leeren: nur gesetzt, wenn die Action es der Person erlaubt (Rolle admin, dev/verify); sonst `null` (Anbieten = Erlauben). */
  leereAlle: (() => Promise<PostfachAntwortForm<{ geloescht: number }>>) | null;
}

export interface PostfachPilleProps {
  /** Stage (Hinweis „Nur Stage“), sonst „Nur Dev“. */
  istStage: boolean;
  /** Ungelesene Nachrichten der gezeigten Person; `null` = nicht ausgeliefert (Impersonation: der Merker gehört der echten Person, r176/r177). */
  ungelesen: number | null;
  /** Aufbewahrung in Tagen (Hinweissatz). */
  aufbewahrungTage: number;
  aktionen: PostfachAktionen;
}

/** Feste Sätze (r105: Ablehnung über das Ziel, Fehlschlag über den Vorgang, nie derselbe Satz, ohne Code). */
export const POSTFACH_ABGELEHNT_SATZ = "Dieses Postfach steht nicht zur Verfügung.";
export const POSTFACH_FEHLSCHLAG_LISTE = "Das Postfach konnte nicht geladen werden. Bitte erneut versuchen.";
export const POSTFACH_FEHLSCHLAG_DETAIL = "Die Nachricht konnte nicht geladen werden. Bitte erneut versuchen.";
export const POSTFACH_FEHLSCHLAG_LEEREN = "Das Postfach konnte nicht geleert werden. Bitte erneut versuchen.";
export const POSTFACH_FEHLSCHLAG_PERSONEN = "Die Personenliste konnte nicht geladen werden. Bitte erneut versuchen.";

/**
 * Der Satz zu einer Antwort: Ablehnung = fester Satz, Fehlschlag = wiederholbarer Satz. Der Wortlaut des Servers wird nie
 * durchgereicht (kein roher Code, kein fremder Satz); nur die Art entscheidet (SG-9).
 */
export function satzFuerAntwort(antwort: { ok: false; art: "abgelehnt" | "fehlgeschlagen" }, fehlschlagSatz: string): string {
  return antwort.art === "abgelehnt" ? POSTFACH_ABGELEHNT_SATZ : fehlschlagSatz;
}

/** Abgekoppeltes Fenster (W119 a): Pfad der Seite, benanntes Fenster (erneuter Klick fokussiert dasselbe), Merkmale des Popups. */
export const POSTFACH_FENSTER_PFAD = "/dev/postfach";
export const POSTFACH_FENSTER_NAME = "hornhub-postfach";
export const POSTFACH_FENSTER_MERKMALE = "popup,width=1100,height=800";
/** Takt der Auto-Aktualisierung im abgekoppelten Fenster, nur für das EIGENE Postfach. */
export const POSTFACH_AUTO_MS = 15_000;
export const POSTFACH_POPUP_GEBLOCKT_SATZ = "Der Browser hat das neue Fenster blockiert.";

/**
 * Auto-Aktualisierung (W119 a, Punkt 3): nur im eigenen Postfach und nur bei sichtbarem Tab. Ein Neuladen eines FREMDEN Postfachs
 * schreibt serverseitig je Abruf einen Audit-Eintrag (`dev_postfach.fremd_eingesehen`); im Takt wäre das eine Protokoll-Flut,
 * darum dort nur per Knopf (bewusste Handlung). Keine Zählung, kein Merker über Aktivität (r177).
 */
export function autoAktualisierenErlaubt(istEigen: boolean, sichtbarkeit: string): boolean {
  return istEigen && sichtbarkeit === "visible";
}

/**
 * Portal-Link aus dem abgekoppelten Fenster im Hauptfenster öffnen (W119 a, Punkt 4): nur wenn ein Opener da ist, nicht
 * geschlossen und gleiche Origin (ein Zugriff auf `location.origin` eines fremden Openers wirft und gilt als „nein“). `true` =
 * im Hauptfenster geöffnet und fokussiert, `false` = der Aufrufer öffnet einen neuen Tab. `ziel` ist ein bewerteter Portal-Link
 * (Portalpfad oder gleiche Origin, `extrahiereLinks`).
 */
export function oeffneImHauptfenster(ziel: string, fenster: { opener?: unknown; location: { origin: string } }): boolean {
  const opener = fenster.opener as { closed?: boolean; focus?: () => void; location?: { origin?: string; assign?: (u: string) => void } } | null | undefined;
  if (!opener || opener.closed) return false;
  try {
    if (opener.location?.origin !== fenster.location.origin || typeof opener.location?.assign !== "function") return false;
    opener.location.assign(ziel);
    opener.focus?.();
    return true;
  } catch {
    return false;
  }
}

export const KANAELE: { id: PostfachKanal; label: string }[] = [
  { id: "mail", label: "E-Mail" },
  { id: "kalender", label: "Kalender" },
  { id: "teams", label: "Teams" },
  { id: "push", label: "Push" },
  { id: "glocke", label: "Glocke" },
  { id: "webhook", label: "Webhook" },
];

export function kanalLabel(kanal: PostfachKanal): string {
  return KANAELE.find((k) => k.id === kanal)?.label ?? kanal;
}

export type ZeitraumId = "alle" | "tag" | "woche";
export const ZEITRAEUME: { id: ZeitraumId; label: string }[] = [
  { id: "alle", label: "Alle (14 Tage)" },
  { id: "tag", label: "Letzte 24 Stunden" },
  { id: "woche", label: "Letzte 7 Tage" },
];

/** ISO-Zeitpunkt der Untergrenze eines Zeitraums (Server filtert; `undefined` = keine Grenze). */
export function zeitraumVon(id: ZeitraumId, jetzt: Date): string | undefined {
  if (id === "tag") return new Date(jetzt.getTime() - 24 * 3_600_000).toISOString();
  if (id === "woche") return new Date(jetzt.getTime() - 7 * 24 * 3_600_000).toISOString();
  return undefined;
}

/** Pillenzahl: nichts bei 0 oder unbekannt, „99+“ ab 100. */
export function pillenZahl(ungelesen: number | null): string | null {
  if (ungelesen === null || ungelesen <= 0) return null;
  return ungelesen > 99 ? "99+" : String(ungelesen);
}

/** Zugänglicher Name der Pille (die Zahl steht mit drin, weil mobil nur Symbol und Zahl sichtbar sind). */
export function pillenLabel(ungelesen: number | null): string {
  const z = pillenZahl(ungelesen);
  return z ? `Virtuelles Postfach öffnen, ${ungelesen} ungelesen` : "Virtuelles Postfach öffnen";
}

/** „10:42“ am selben Tag, „Gestern“, sonst „11.10.“ (Zeitzone der Anzeige = Browser; Server liefert ISO). */
export function zeitKurz(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tag = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (tag(d) === tag(jetzt)) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const gestern = new Date(jetzt.getTime() - 24 * 3_600_000);
  if (tag(d) === tag(gestern)) return "Gestern";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
}

/** „13.10. 10:42“ (Detailkopf). */
export function zeitLang(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}. ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function statusLabel(status: string): string {
  return status === "zugestellt_allowlist" ? "zugestellt (Allowlist)" : "nicht zugestellt";
}

export function personAnzeigeName(p: PostfachPerson): string {
  const name = p.name?.trim() || "Ohne Namen";
  const mitDu = p.eigen ? `${name} (du)` : name;
  return typeof p.anzahl === "number" ? `${mitDu} (${p.anzahl})` : mitDu;
}

export function groesseLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * Inhalt der abgeschotteten Mail-Darstellung (`<iframe sandbox="" srcdoc>`, ohne `allow-scripts` und ohne `allow-same-origin`).
 * Mehrere Schichten, damit nichts nach außen geht (R6 Tracking-Pixel):
 *  1. CSP als erstes Element im Dokument: `default-src 'none'; img-src data:`; Formulare nie (`form-action 'none'`).
 *  2. Elemente, die ohne Skript nach außen navigieren oder laden, werden aus dem Mail-Inhalt entfernt: `meta` (Refresh),
 *     `base`, `link`, `iframe`/`frame`/`object`/`embed`, `script`, `form`.
 *  3. Der Rahmen selbst hat `sandbox=""` (kein Skript, kein Formular, keine Navigation des Hauptfensters, keine Pop-ups).
 *  4. Links sind nicht navigierbar: `<a href="X">Text</a>` wird zu „Text (X)“ als reiner Text (die Adresse bleibt lesbar), alle
 *     übrigen Ziel-Attribute (`href`, `src` außer `data:`, `action`, `formaction`, `xlink:href`, `poster`, `background`, `srcset`,
 *     `ping`) werden entfernt; zusätzlich `a{pointer-events:none}`. Ein Klick kann den Rahmen also nie zu einer fremden Adresse führen.
 * Die Entfernung läuft bis nichts mehr ersetzt wird (Verschachtelung wie `<scr<script></script>ipt>` hilft nicht).
 * Sie ist ein Zusatz, nicht der Schutz: der Schutz ist der Rahmen mit CSP.
 */
const MAIL_ELEMENTE = /<\s*(script|iframe|frame|frameset|object|embed|form|base|link|meta)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
const MAIL_ELEMENTE_EINZEL = /<\s*(script|iframe|frame|frameset|object|embed|form|base|link|meta)\b[^>]*\/?>/gi;
const MAIL_LINK = /<a\b[^>]*?\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*))[^>]*>([\s\S]*?)<\s*\/\s*a\s*>/gi;
const MAIL_ZIELATTRIBUT = /\s(?:href|xlink:href|action|formaction|poster|background|srcset|ping|src)\s*=\s*(?:"(?!data:)[^"]*"|'(?!data:)[^']*'|(?!data:)[^\s>"']+)/gi;

function textEscape(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function bis(html: string, schritt: (s: string) => string): string {
  let s = html;
  for (let i = 0; i < 10; i++) {
    const n = schritt(s);
    if (n === s) return s;
    s = n;
  }
  return s;
}

export function mailSrcdoc(html: string): string {
  const bereinigt = bis(html, (s) =>
    s
      .replace(MAIL_ELEMENTE, "")
      .replace(MAIL_ELEMENTE_EINZEL, "")
      .replace(MAIL_LINK, (_m, a: string | undefined, b: string | undefined, c: string | undefined, text: string) => `<span>${text} (${textEscape(a ?? b ?? c ?? "")})</span>`)
      .replace(MAIL_ZIELATTRIBUT, ""),
  );
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'\">" +
    "<style>body{margin:0;padding:16px;font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;color:CanvasText;background:Canvas;word-wrap:break-word}img{max-width:100%;height:auto}a{pointer-events:none}</style>" +
    "</head><body>" +
    bereinigt +
    "</body></html>"
  );
}

/**
 * Linkliste unter der Mail („Links in dieser Nachricht“, Betreiber W116 b). Die Mail-Darstellung bleibt abgeschottet und ohne
 * klickbare Links (`mailSrcdoc`); die Liste ist die einzige Stelle, an der ein Ziel angeboten wird. Reine Funktion ohne DOM.
 * Eingabe ist nur, was der Server schon ausgeliefert hat (`detail.html` bzw. `detail.text` nach der Schwärzung, PQ9): ein
 * geschwärzter Zugangslink steht dort als Platzhaltertext und ist nie ein Ziel. Angeboten werden nur `http(s)://…` und
 * Portalpfade (`/…`); jedes andere Schema (`javascript:`, `data:`, `vbscript:`, `file:`, `mailto:` …) bleibt unsichtbar.
 */
export interface MailLink {
  /** Das Ziel, wie es in der Mail steht (Entities dekodiert); Anzeige und `href` sind dasselbe. */
  ziel: string;
  /** Linktext der Mail; ohne Text das Ziel. */
  text: string;
  /** Portal (gleiche Origin oder Portalpfad): öffnet im selben Tab. Sonst extern: neuer Tab. */
  portal: boolean;
}

export interface MailLinkListe {
  links: MailLink[];
  /** Wie viele Links über der Obergrenze lagen und nicht angeboten werden. */
  weitere: number;
}

export const MAIL_LINKS_MAX = 50;
const ZIEL_MAX = 2000;
const TEXT_MAX = 120;

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };

/** Einmal dekodieren (kein rekursives Dekodieren: `&amp;#106;` bleibt `&#106;`). */
function entitiesDekodieren(s: string): string {
  return s.replace(/&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z]{2,6}));/g, (m, dez: string | undefined, hex: string | undefined, name: string | undefined) => {
    if (name !== undefined) return ENTITIES[name.toLowerCase()] ?? m;
    const cp = dez !== undefined ? Number.parseInt(dez, 10) : Number.parseInt(hex ?? "", 16);
    if (!Number.isFinite(cp) || cp <= 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return m;
    return String.fromCodePoint(cp);
  });
}

/**
 * Prüft ein rohes Ziel. `null` = nicht anbieten. Streng: keine Leerzeichen/Steuerzeichen/Backslashes (Browser würden sie
 * entfernen oder umdeuten), keine Zugangsdaten in der Adresse, nur `http`/`https` oder ein einzelner führender `/`.
 */
function bewerteZiel(roh: string, portalOrigin: string): { ziel: string; portal: boolean } | null {
  const ziel = entitiesDekodieren(roh).trim();
  if (ziel === "" || ziel.length > ZIEL_MAX) return null;
  if (/[\s\u0000-\u001f\u007f\\]/.test(ziel)) return null;
  if (ziel.startsWith("/")) {
    if (ziel.startsWith("//")) return null;
    return { ziel, portal: true };
  }
  if (!/^https?:\/\//i.test(ziel)) return null;
  let url: URL;
  try {
    url = new URL(ziel);
  } catch {
    return null;
  }
  if ((url.protocol !== "http:" && url.protocol !== "https:") || url.hostname === "" || url.username !== "" || url.password !== "") return null;
  return { ziel, portal: portalOrigin !== "" && url.origin === portalOrigin };
}

const LINK_TOKEN = /<!--[\s\S]*?-->|<(script|style)\b[\s\S]*?<\/\1\s*>|<\/\s*a\s*>|<a\b[^>]*>|<[^>]*>/gi;
const HREF_ATTR = /\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*))/i;
const KLARTEXT_URL = /https?:\/\/[^\s"'<>]+/gi;

function kuerzen(s: string): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > TEXT_MAX ? `${t.slice(0, TEXT_MAX - 1)}…` : t;
}

/**
 * Alle Links einer Nachricht in Reihenfolge der Mail, Duplikate (gleiches Ziel) zusammengefasst, höchstens `MAIL_LINKS_MAX`.
 * `html` gesetzt: Anker (`<a href>`) plus Klartext-URLs im sichtbaren Text; sonst die Klartext-URLs aus `text`.
 * `portalOrigin`: Origin des Portals (`window.location.origin`); leer = nur relative Pfade gelten als Portal.
 */
export function extrahiereLinks(html: string | null, text: string, portalOrigin: string): MailLinkListe {
  const gefunden: MailLink[] = [];
  const gesehen = new Set<string>();
  const merke = (rohZiel: string, linkText: string) => {
    const b = bewerteZiel(rohZiel, portalOrigin);
    if (!b || gesehen.has(b.ziel)) return;
    gesehen.add(b.ziel);
    gefunden.push({ ziel: b.ziel, text: kuerzen(linkText) || b.ziel, portal: b.portal });
  };
  const klartext = (s: string) => {
    for (const m of s.matchAll(KLARTEXT_URL)) merke(m[0].replace(/[.,;:!?)\]}]+$/, ""), "");
  };

  if (html === null) {
    klartext(text);
  } else {
    // Offener Anker: Ziel merken, Text einsammeln bis `</a>`, dem nächsten Anker oder dem Ende (kaputtes HTML bleibt lesbar).
    let offen: { href: string | null; text: string } | null = null;
    const schliesse = () => {
      if (offen?.href != null) merke(offen.href, entitiesDekodieren(offen.text));
      offen = null;
    };
    let pos = 0;
    for (const m of html.matchAll(LINK_TOKEN)) {
      const davor = html.slice(pos, m.index);
      if (offen) offen.text += davor;
      else klartext(entitiesDekodieren(davor));
      pos = (m.index ?? 0) + m[0].length;
      if (/^<\/\s*a\s*>$/i.test(m[0])) schliesse();
      else if (/^<a\b/i.test(m[0])) {
        schliesse();
        const h = HREF_ATTR.exec(m[0]);
        offen = { href: h ? (h[1] ?? h[2] ?? h[3] ?? null) : null, text: "" };
      }
    }
    const rest = html.slice(pos);
    if (offen) (offen as { text: string }).text += rest;
    else klartext(entitiesDekodieren(rest));
    schliesse();
  }

  return { links: gefunden.slice(0, MAIL_LINKS_MAX), weitere: Math.max(0, gefunden.length - MAIL_LINKS_MAX) };
}
